/**
 * Element Attribute Mapping System
 * 
 * This system defines which element attributes can have which media types,
 * enabling dynamic content binding and proper UI component selection.
 */

import { MediaType } from "@/page-components/post-generator/core/template.schemas";

/**
 * Defines an element attribute that can be bound to dynamic content
 */
export type ElementAttributeDef = {
  /** Unique key for the attribute (e.g., 'content', 'color', 'source') */
  attributeKey: string;
  /** Display label for UI (e.g., 'Text Content', 'Text Color', 'Image Source') */
  attributeLabel: string;
  /** Compatible media types for this attribute */
  compatibleMediaTypes: MediaType[];
  /** Default value for the attribute */
  defaultValue?: any;
  /** Whether this attribute is required */
  required?: boolean;
  /** UI component for template editor (static/dynamic selection) */
  editorComponent: string;
  /** UI component for post generator (concrete entity selection) */
  generatorComponent: string;
  /** Additional metadata */
  meta?: {
    description?: string;
    category?: string;
    priority?: number;
  };
};

/**
 * Defines all bindable attributes for an element type
 */
export type ElementAttributeMapping = {
  /** Element type identifier (e.g., 'text', 'rect', 'circle', 'image') */
  elementType: string;
  /** Display name for the element type */
  displayName: string;
  /** Array of bindable attributes */
  attributes: ElementAttributeDef[];
};

/**
 * Registry for managing element attribute mappings
 */
export class ElementAttributeRegistry {
  private mappings = new Map<string, ElementAttributeMapping>();

  /**
   * Register element attribute mapping
   */
  public register(mapping: ElementAttributeMapping): void {
    if (this.mappings.has(mapping.elementType)) {
      throw new Error(`Element type '${mapping.elementType}' is already registered`);
    }
    this.mappings.set(mapping.elementType, mapping);
  }

  /**
   * Get attribute mapping for element type
   */
  public get(elementType: string): ElementAttributeMapping | undefined {
    return this.mappings.get(elementType);
  }

  /**
   * Get all registered mappings
   */
  public getAll(): ElementAttributeMapping[] {
    return Array.from(this.mappings.values());
  }

  /**
   * Get bindable attributes for element type
   */
  public getAttributes(elementType: string): ElementAttributeDef[] {
    const mapping = this.get(elementType);
    return mapping?.attributes || [];
  }

  /**
   * Get specific attribute definition
   */
  public getAttribute(elementType: string, attributeKey: string): ElementAttributeDef | undefined {
    const mapping = this.get(elementType);
    return mapping?.attributes.find(attr => attr.attributeKey === attributeKey);
  }

  /**
   * Find element types that support a specific media type
   */
  public findByMediaType(mediaType: MediaType): ElementAttributeMapping[] {
    return this.getAll().filter(mapping =>
      mapping.attributes.some(attr => attr.compatibleMediaTypes.includes(mediaType))
    );
  }

  /**
   * Check if element attribute supports media type
   */
  public isCompatible(elementType: string, attributeKey: string, mediaType: MediaType): boolean {
    const attribute = this.getAttribute(elementType, attributeKey);
    return attribute?.compatibleMediaTypes.includes(mediaType) || false;
  }

  /**
   * Clear all mappings
   */
  public clear(): void {
    this.mappings.clear();
  }

  /**
   * Get count of registered mappings
   */
  public size(): number {
    return this.mappings.size;
  }
}